/**
 * 图片重载管理器
 * 监听页面可见性变化和网络状态变化，自动重新加载失败的图片
 */

class ImageReloadManager {
  private isOnline: boolean = navigator.onLine;
  private isPageVisible: boolean = !document.hidden;
  private reloadCallbacks: Set<() => void> = new Set();

  constructor() {
    this.init();
  }

  private init() {
    // 监听网络状态变化
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    // 监听页面可见性变化
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.triggerReload("network restored");
  };

  private handleOffline = () => {
    this.isOnline = false;
  };

  private handleVisibilityChange = () => {
    const isVisible = !document.hidden;

    if (isVisible && !this.isPageVisible) {
      this.isPageVisible = true;

      // 只有在网络在线时才触发重载
      if (this.isOnline) {
        this.triggerReload("page visible");
      }
    } else if (!isVisible) {
      this.isPageVisible = false;
    }
  };

  private triggerReload(reason: string) {
    // 触发所有注册的回调
    this.reloadCallbacks.forEach(callback => {
      try {
        callback();
      } catch {
        // 静默处理错误
      }
    });

    // 发送自定义事件，让图片组件监听
    window.dispatchEvent(
      new CustomEvent("image-reload-trigger", {
        detail: { reason },
      })
    );
  }

  /**
   * 注册重载回调函数
   */
  registerReloadCallback(callback: () => void): () => void {
    this.reloadCallbacks.add(callback);

    // 返回取消注册的函数
    return () => {
      this.reloadCallbacks.delete(callback);
    };
  }

  /**
   * 手动触发重载
   */
  manualReload() {
    if (this.isOnline) {
      this.triggerReload("manual trigger");
    }
  }

  /**
   * 获取当前状态
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      isPageVisible: this.isPageVisible,
    };
  }

  /**
   * 销毁管理器
   */
  destroy() {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    this.reloadCallbacks.clear();
  }
}

// 导出单例
export const imageReloadManager = new ImageReloadManager();
